import { useCallback, useMemo, useState, useEffect } from "react";
import {
  ReactFlow, Background, Controls, MiniMap, addEdge, useNodesState, useEdgesState,
  Handle, Position, ReactFlowProvider, useReactFlow,
  BaseEdge, EdgeLabelRenderer, getBezierPath,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  Play, SpeakerHigh, Ear, WaveTriangle, GitBranch, PhoneCall, PhoneX, X,
  Plus, Trash, UploadSimple, WarningCircle, Hash, Flag,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { API, formatApiError } from "@/lib/api";

export const KIND = {
  start: { label: "Start", color: "#22c55e", icon: Play },
  play_audio: { label: "Play Audio", color: "#6366f1", icon: SpeakerHigh },
  wait: { label: "Wait for Customer", color: "#f59e0b", icon: Ear,
    outputs: [{ id: "speech", label: "Speech" }, { id: "silence", label: "Silence" }] },
  intent: { label: "Customer Response", color: "#a855f7", icon: GitBranch, dynamic: true },
  dtmf: { label: "DTMF", color: "#0ea5e9", icon: Hash },
  silence: { label: "Silence", color: "#64748b", icon: WaveTriangle,
    outputs: [{ id: "retry", label: "Retry" }, { id: "max_reached", label: "Max reached" }] },
  transfer: { label: "Transfer", color: "#14b8a6", icon: PhoneCall,
    outputs: [{ id: "success", label: "Success" }, { id: "failure", label: "Failure" }] },
  hangup: { label: "Hang Up", color: "#ef4444", icon: PhoneX },
  end: { label: "End", color: "#334155", icon: Flag, terminal: true },
};

const PALETTE = ["play_audio", "wait", "silence", "intent", "dtmf", "transfer", "hangup", "end"];
const DTMF_DIGITS = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "0", "*", "#"];

function defaultConfig(kind) {
  switch (kind) {
    case "play_audio": return { source: "upload", prompt_text: "", language: "en-US", voice: "female" };
    case "wait": return { timeout_seconds: 5, dtmf_enabled: false };
    case "silence": return { max_attempts: 2 };
    case "dtmf": return { keys: [{ id: "k1", digit: "1" }] };
    case "end": return {};
    case "intent": return { responses: [
      { id: "interested", label: "Interested", phrases: ["yes", "interested"] },
      { id: "not_interested", label: "Not Interested", phrases: ["no", "not interested"] },
      { id: "callback", label: "Callback", phrases: ["call later"] },
      { id: "unknown", label: "Unknown", phrases: [] },
    ] };
    case "transfer": return { transfer_type: "sip",
      sip: { host: "", port: 5060, transport: "UDP", destination: "", extra: "", auth_username: "", auth_password: "" },
      did: { number: "" } };
    case "hangup": return { reason: "" };
    default: return {};
  }
}

function outputsOf(node) {
  const kind = node.data.kind;
  const cfg = KIND[kind];
  if (kind === "intent") return (node.data.config?.responses || []).map((r) => ({ id: r.id, label: r.label }));
  if (kind === "dtmf") return (node.data.config?.keys || []).map((k) => ({ id: k.id, label: k.digit }));
  if (kind === "wait") {
    const outs = [{ id: "speech", label: "Speech" }, { id: "silence", label: "Silence" }];
    if (node.data.config?.dtmf_enabled) outs.push({ id: "dtmf", label: "DTMF" });
    return outs;
  }
  if (cfg?.outputs) return cfg.outputs;
  if (cfg?.terminal) return [];
  return [{ id: null, label: null }]; // single unnamed output (start, play_audio, hangup)
}

function handleLabel(node, handleId) {
  if (!handleId) return undefined;
  return outputsOf(node).find((o) => o.id === handleId)?.label;
}

// ---------------------------------------------------------------------------
// Deletable edge — click the connection to reveal a delete (×) button that
// disconnects it. Used for both admin & customer designers (disabled in readOnly).
// ---------------------------------------------------------------------------
function DeletableEdge({ id, sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, label, markerEnd, style, selected, data }) {
  const { setEdges } = useReactFlow();
  const readOnly = !!data?.readOnly;
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX, sourceY, sourcePosition, targetX, targetY, targetPosition,
  });
  const del = (evt) => {
    evt.stopPropagation();
    setEdges((eds) => eds.filter((e) => e.id !== id));
    toast.success("Connection removed");
  };
  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd}
        style={{ ...style, strokeWidth: selected ? 2.5 : 1.5, stroke: selected ? "hsl(var(--primary))" : (style?.stroke || "#b1b1b7") }} />
      <EdgeLabelRenderer>
        <div className="nodrag nopan group" data-testid={`flow-edge-${id}`}
          style={{ position: "absolute", transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`, pointerEvents: "all" }}>
          <div className="flex items-center gap-1">
            {label && <span className="rounded bg-background/90 border border-border px-1.5 py-0.5 text-[10px] text-muted-foreground shadow-sm whitespace-nowrap">{label}</span>}
            {!readOnly && (
              <button onClick={del} title="Delete connection" aria-label="Delete connection" data-testid={`delete-edge-${id}`}
                className={`w-4 h-4 rounded-full grid place-items-center bg-background border shadow-sm transition-opacity ${selected ? "opacity-100 border-destructive text-destructive" : "opacity-0 group-hover:opacity-100 border-border text-muted-foreground hover:text-destructive hover:border-destructive"}`}>
                <X className="w-2.5 h-2.5" weight="bold" />
              </button>
            )}
          </div>
        </div>
      </EdgeLabelRenderer>
    </>
  );
}


// ---------------------------------------------------------------------------
// Node renderer
// ---------------------------------------------------------------------------
function FlowNode({ id, data, selected }) {
  const cfg = KIND[data.kind] || KIND.play_audio;
  const Icon = cfg.icon;
  const { setNodes, setEdges } = useReactFlow();
  const outs = outputsOf({ id, data });
  const remove = () => {
    setNodes((nds) => nds.filter((n) => n.id !== id));
    setEdges((eds) => eds.filter((e) => e.source !== id && e.target !== id));
  };

  const summary = (() => {
    const c = data.config || {};
    if (data.kind === "play_audio") return c.audio_filename || (c.prompt_text ? `“${c.prompt_text.slice(0, 26)}”` : "No audio set");
    if (data.kind === "wait") return `Timeout ${c.timeout_seconds ?? 5}s`;
    if (data.kind === "silence") return `Max ${c.max_attempts ?? 2} attempts`;
    if (data.kind === "intent") return `${(c.responses || []).length} responses`;
    if (data.kind === "transfer") return c.transfer_type === "did" ? `DID ${c.did?.number || "—"}` : `SIP ${c.sip?.destination || "—"}`;
    if (data.kind === "hangup") return c.reason ? `Reason: ${c.reason}` : "Hang up";
    if (data.kind === "dtmf") return `${(c.keys || []).length} key${(c.keys || []).length === 1 ? "" : "s"}`;
    if (data.kind === "end") return "Flow complete";
    return null;
  })();

  return (
    <div className={`rounded-xl border-2 bg-card shadow-sm group transition-shadow ${selected ? "ring-2 ring-offset-1 ring-primary" : ""}`}
      style={{ borderColor: cfg.color, minWidth: outs.length > 1 ? Math.max(210, outs.length * 96) : 210 }} data-testid={`flow-node-${data.kind}`}>
      {data.kind !== "start" && <Handle type="target" position={Position.Top} style={{ background: cfg.color, width: 10, height: 10 }} />}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div className="w-7 h-7 rounded-lg grid place-items-center text-white shrink-0" style={{ background: cfg.color }}>
          <Icon className="w-4 h-4" weight="bold" />
        </div>
        <div className="leading-tight min-w-0">
          <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{cfg.label}</p>
          <p className="text-sm font-medium truncate">{data.label || cfg.label}</p>
        </div>
        {data.kind !== "start" && (
          <button onClick={remove} data-testid={`delete-node-${id}`}
            className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity w-5 h-5 rounded grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10">
            <X className="w-3.5 h-3.5" weight="bold" />
          </button>
        )}
      </div>
      {summary && <p className="px-3 pb-2 -mt-1 text-[11px] text-muted-foreground truncate">{summary}</p>}

      {outs.length > 1 ? (
        <div className="flex justify-between px-3 pb-2 gap-1 relative" style={{ minHeight: 26 }}>
          {outs.map((o, i) => (
            <div key={o.id} className="flex-1 text-center relative">
              <span className="text-[9px] leading-tight text-muted-foreground block px-0.5">{o.label}</span>
              <Handle type="source" id={o.id} position={Position.Bottom}
                style={{ left: `${(i + 0.5) * (100 / outs.length)}%`, background: cfg.color, width: 10, height: 10 }} />
            </div>
          ))}
        </div>
      ) : outs.length === 1 ? (
        <Handle type="source" position={Position.Bottom} style={{ background: cfg.color, width: 10, height: 10 }} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Audio player (auth via query param, since <audio> can't send headers)
// ---------------------------------------------------------------------------
function AudioPreview({ fileId }) {
  const token = localStorage.getItem("wa_token");
  return <audio controls src={`${API}/audio/${fileId}?auth=${token}`} className="w-full mt-2 h-9" data-testid="audio-preview" />;
}

// ---------------------------------------------------------------------------
// Config panel
// ---------------------------------------------------------------------------
function ConfigPanel({ node, setNodes, setEdges, onClose }) {
  const [uploading, setUploading] = useState(false);
  const kind = node.data.kind;
  const cfg = node.data.config || {};

  const setData = (dataPatch) => setNodes((nds) => nds.map((n) => n.id === node.id ? { ...n, data: { ...n.data, ...dataPatch } } : n));
  const patch = (cfgPatch) => setNodes((nds) => nds.map((n) => n.id === node.id ? { ...n, data: { ...n.data, config: { ...n.data.config, ...cfgPatch } } } : n));

  const uploadAudio = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/audio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      let duration = null;
      try {
        duration = await new Promise((res, rej) => {
          const a = document.createElement("audio");
          a.preload = "metadata";
          a.onloadedmetadata = () => res(Math.round(a.duration));
          a.onerror = rej;
          a.src = URL.createObjectURL(f);
        });
      } catch { /* duration optional */ }
      patch({ source: "upload", audio_file_id: data.id, audio_filename: data.original_filename, duration,
              audio_warning: data.warning || null, telephony_compatible: data.telephony_compatible });
      if (data.warning) toast.warning(data.warning);
      else toast.success("Audio uploaded (telephony-compatible)");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const addResponse = () => {
    const id = `r_${Date.now().toString(36)}`;
    patch({ responses: [...(cfg.responses || []), { id, label: "New response", phrases: [] }] });
  };
  const updateResponse = (rid, key, val) =>
    patch({ responses: (cfg.responses || []).map((r) => r.id === rid ? { ...r, [key]: val } : r) });
  const deleteResponse = (rid) => {
    patch({ responses: (cfg.responses || []).filter((r) => r.id !== rid) });
    setEdges((eds) => eds.filter((e) => !(e.source === node.id && e.sourceHandle === rid)));
  };

  const addKey = () => patch({ keys: [...(cfg.keys || []), { id: `k_${Date.now().toString(36)}`, digit: "1" }] });
  const updateKey = (kid, digit) => patch({ keys: (cfg.keys || []).map((k) => k.id === kid ? { ...k, digit } : k) });
  const deleteKey = (kid) => {
    patch({ keys: (cfg.keys || []).filter((k) => k.id !== kid) });
    setEdges((eds) => eds.filter((e) => !(e.source === node.id && e.sourceHandle === kid)));
  };
  const toggleWaitDtmf = (enabled) => {
    patch({ dtmf_enabled: enabled });
    if (!enabled) setEdges((eds) => eds.filter((e) => !(e.source === node.id && e.sourceHandle === "dtmf")));
  };

  return (
    <div className="w-80 shrink-0 border-l border-border pl-4 overflow-y-auto" data-testid="node-config-panel">
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold">{KIND[kind]?.label} settings</p>
        <button onClick={onClose} data-testid="close-config-panel" className="w-6 h-6 rounded grid place-items-center hover:bg-muted"><X className="w-4 h-4" /></button>
      </div>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Node label</Label>
          <Input data-testid="config-label-input" value={node.data.label || ""} onChange={(e) => setData({ label: e.target.value })} />
        </div>

        {kind === "start" && <p className="text-xs text-muted-foreground">The entry point of the call. No configuration needed.</p>}

        {kind === "play_audio" && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Prerecorded audio (WAV, 8 kHz mono PCM/G.711 preferred)</Label>
              <label className="flex items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border p-3 text-sm cursor-pointer hover:bg-muted/50" data-testid="audio-upload-label">
                <UploadSimple className="w-4 h-4" weight="bold" />
                {uploading ? "Uploading…" : (cfg.audio_filename || "Choose audio file")}
                <input type="file" accept="audio/*" className="hidden" onChange={uploadAudio} data-testid="audio-upload-input" disabled={uploading} />
              </label>
              {cfg.audio_file_id && <AudioPreview fileId={cfg.audio_file_id} />}
              {cfg.duration != null && <p className="text-[11px] text-muted-foreground">Duration ~{cfg.duration}s</p>}
              {cfg.audio_warning && (
                <p className="text-[11px] text-[hsl(var(--warning))] flex items-start gap-1" data-testid="audio-warning">
                  <WarningCircle className="w-3.5 h-3.5 mt-0.5 shrink-0" weight="fill" />{cfg.audio_warning}
                </p>
              )}
              {cfg.audio_file_id && cfg.telephony_compatible && <p className="text-[11px] text-[hsl(var(--success))]">✓ Telephony-compatible (8 kHz mono).</p>}
            </div>
          </>
        )}

        {kind === "wait" && (
          <div className="space-y-2">
            <div className="space-y-1.5">
              <Label className="text-xs">Maximum wait time (seconds)</Label>
              <Input data-testid="wait-timeout" type="number" min={1} value={cfg.timeout_seconds ?? 5} onChange={(e) => patch({ timeout_seconds: Number(e.target.value) })} />
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input type="checkbox" data-testid="wait-dtmf-toggle" checked={!!cfg.dtmf_enabled} onChange={(e) => toggleWaitDtmf(e.target.checked)} />
              Accept DTMF key press (adds a DTMF branch)
            </label>
            <p className="text-[11px] text-muted-foreground">Branches: <b>Speech</b>, <b>Silence</b> (timeout){cfg.dtmf_enabled ? ", " : ""}{cfg.dtmf_enabled && <b>DTMF</b>}.</p>
          </div>
        )}

        {kind === "silence" && (
          <div className="space-y-1.5">
            <Label className="text-xs">Max re-prompt attempts</Label>
            <Input data-testid="silence-attempts" type="number" min={1} value={cfg.max_attempts ?? 2} onChange={(e) => patch({ max_attempts: Number(e.target.value) })} />
            <p className="text-[11px] text-muted-foreground mt-1"><b>Retry</b> re-prompts the customer; <b>Max reached</b> fires after the limit.</p>
          </div>
        )}

        {kind === "intent" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Response options (branches)</Label>
              <Button size="sm" variant="outline" className="h-7 rounded-lg" onClick={addResponse} data-testid="intent-add-response"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
            </div>
            {(cfg.responses || []).map((r) => (
              <div key={r.id} className="rounded-lg border border-border p-2.5 space-y-2" data-testid={`intent-response-${r.id}`}>
                <div className="flex gap-1.5">
                  <Input className="h-8" data-testid={`intent-response-label-${r.id}`} value={r.label} onChange={(e) => updateResponse(r.id, "label", e.target.value)} placeholder="Interested" />
                  <button onClick={() => deleteResponse(r.id)} data-testid={`intent-response-delete-${r.id}`} className="w-8 h-8 rounded grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"><Trash className="w-4 h-4" /></button>
                </div>
                <PhrasesInput
                  testid={`intent-response-phrases-${r.id}`}
                  value={r.phrases}
                  onCommit={(arr) => updateResponse(r.id, "phrases", arr)} />
              </div>
            ))}
            {(cfg.responses || []).length === 0 && <p className="text-[11px] text-destructive">Add at least one response option.</p>}
          </div>
        )}

        {kind === "dtmf" && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Expected keys (each is a branch)</Label>
              <Button size="sm" variant="outline" className="h-7 rounded-lg" onClick={addKey} data-testid="dtmf-add-key"><Plus className="w-3.5 h-3.5 mr-1" /> Add</Button>
            </div>
            {(cfg.keys || []).map((k) => (
              <div key={k.id} className="flex gap-1.5 items-center" data-testid={`dtmf-key-${k.id}`}>
                <Select value={k.digit} onValueChange={(v) => updateKey(k.id, v)}>
                  <SelectTrigger data-testid={`dtmf-key-select-${k.id}`} className="h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>{DTMF_DIGITS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
                <span className="text-xs text-muted-foreground flex-1">Press “{k.digit}”</span>
                <button onClick={() => deleteKey(k.id)} data-testid={`dtmf-key-delete-${k.id}`} className="w-8 h-8 rounded grid place-items-center text-muted-foreground hover:text-destructive hover:bg-destructive/10"><Trash className="w-4 h-4" /></button>
              </div>
            ))}
            {(cfg.keys || []).length === 0 && <p className="text-[11px] text-destructive">Add at least one key (0–9, * or #).</p>}
            <p className="text-[11px] text-muted-foreground">Each configured key gets its own outgoing branch (executed by FreeSWITCH later).</p>
          </div>
        )}

        {kind === "transfer" && (
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Transfer type</Label>
              <Select value={cfg.transfer_type || "sip"} onValueChange={(v) => patch({ transfer_type: v })}>
                <SelectTrigger data-testid="transfer-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="sip">External SIP Server</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(cfg.transfer_type || "sip") === "sip" ? (
              <div className="space-y-2">
                <div className="space-y-1.5"><Label className="text-xs">SIP server IP / domain</Label>
                  <Input data-testid="transfer-sip-host" value={cfg.sip?.host || ""} onChange={(e) => patch({ sip: { ...cfg.sip, host: e.target.value } })} placeholder="sip.example.com" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5"><Label className="text-xs">Port</Label>
                    <Input data-testid="transfer-sip-port" type="number" value={cfg.sip?.port ?? 5060} onChange={(e) => patch({ sip: { ...cfg.sip, port: Number(e.target.value) } })} /></div>
                  <div className="space-y-1.5"><Label className="text-xs">Transport</Label>
                    <Select value={cfg.sip?.transport || "UDP"} onValueChange={(v) => patch({ sip: { ...cfg.sip, transport: v } })}>
                      <SelectTrigger data-testid="transfer-sip-transport"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="UDP">UDP</SelectItem><SelectItem value="TCP">TCP</SelectItem><SelectItem value="TLS">TLS</SelectItem></SelectContent>
                    </Select></div>
                </div>
                <div className="space-y-1.5"><Label className="text-xs">Destination / extension</Label>
                  <Input data-testid="transfer-sip-destination" value={cfg.sip?.destination || ""} onChange={(e) => patch({ sip: { ...cfg.sip, destination: e.target.value } })} placeholder="1001" /></div>
                <div className="grid grid-cols-2 gap-2">
                  <div className="space-y-1.5"><Label className="text-xs">SIP auth username (optional)</Label>
                    <Input data-testid="transfer-sip-auth-user" value={cfg.sip?.auth_username || ""} onChange={(e) => patch({ sip: { ...cfg.sip, auth_username: e.target.value } })} placeholder="only if server asks for login" autoComplete="off" /></div>
                  <div className="space-y-1.5"><Label className="text-xs">SIP auth password (optional)</Label>
                    <Input data-testid="transfer-sip-auth-pass" type="password" value={cfg.sip?.auth_password || ""} onChange={(e) => patch({ sip: { ...cfg.sip, auth_password: e.target.value } })} placeholder="only if server asks for login" autoComplete="new-password" /></div>
                </div>
                <p className="text-[11px] text-muted-foreground">Fill these only if your SIP/PBX server requires a login (returns 401/407). Leave blank for IP-trusted servers.</p>
                <div className="space-y-1.5"><Label className="text-xs">Additional SIP params (optional)</Label>
                  <Textarea rows={2} data-testid="transfer-sip-extra" value={cfg.sip?.extra || ""} onChange={(e) => patch({ sip: { ...cfg.sip, extra: e.target.value } })} placeholder="header=value" /></div>
              </div>
            ) : (
              <div className="space-y-1.5"><Label className="text-xs">DID / telephone number</Label>
                <Input data-testid="transfer-did-number" value={cfg.did?.number || ""} onChange={(e) => patch({ did: { ...cfg.did, number: e.target.value } })} placeholder="+14155550123" /></div>
            )}
            <p className="text-[11px] text-muted-foreground">Two branches: <b>Success</b> and <b>Failure</b>. Destination belongs to this node only. SIP/DID execution happens in the later call-engine step.</p>
          </div>
        )}

        {kind === "hangup" && (
          <div className="space-y-1.5">
            <Label className="text-xs">Hangup reason / label (optional)</Label>
            <Input data-testid="hangup-reason" value={cfg.reason || ""} onChange={(e) => patch({ reason: e.target.value })} placeholder="not_interested" />
            <p className="text-[11px] text-muted-foreground">Represents the telephony hangup action. Connect it to an <b>End</b> node to mark flow completion.</p>
          </div>
        )}

        {kind === "end" && (
          <p className="text-xs text-muted-foreground">Terminal node — marks the campaign flow as complete. No configuration needed.</p>
        )}
      </div>
    </div>
  );
}

function validateFlow(nodes, edges) {
  const errors = [];
  const starts = nodes.filter((n) => n.data?.kind === "start");
  if (starts.length === 0) errors.push("The flow must contain a Start node.");
  else if (starts.length > 1) errors.push("The flow must contain exactly one Start node.");

  const incoming = {}, outgoing = {};
  edges.forEach((e) => {
    (outgoing[e.source] = outgoing[e.source] || []).push(e);
    (incoming[e.target] = incoming[e.target] || []).push(e);
  });
  if (starts[0] && !(outgoing[starts[0].id] || []).length) errors.push("The Start node must connect to at least one node.");

  const lbl = (n) => n.data?.label || (n.data?.kind || "node");
  const needsOutgoing = new Set(["play_audio", "wait", "silence", "intent", "dtmf"]);
  nodes.forEach((n) => {
    const kind = n.data?.kind, cfg = n.data?.config || {};
    if (kind !== "start" && !(incoming[n.id] || []).length) errors.push(`“${lbl(n)}” is not connected to the flow.`);
    if (needsOutgoing.has(kind) && !(outgoing[n.id] || []).length) errors.push(`“${lbl(n)}” has no next step — connect it onward to another node or to a Hang Up/End node, otherwise the call drops as a dead end.`);
    if (kind === "intent" && !(cfg.responses || []).length) errors.push(`Customer Response node “${lbl(n)}” must have at least one response option.`);
    if (kind === "dtmf" && !(cfg.keys || []).length) errors.push(`DTMF node “${lbl(n)}” must have at least one configured key.`);
    if (kind === "transfer") {
      const t = cfg.transfer_type;
      if (t === "sip") { if (!cfg.sip?.host?.trim() || !cfg.sip?.destination?.trim()) errors.push(`Transfer node “${lbl(n)}” needs a SIP server and destination.`); }
      else if (t === "did") { if (!cfg.did?.number?.trim()) errors.push(`Transfer node “${lbl(n)}” needs a DID/phone number.`); }
      else errors.push(`Transfer node “${lbl(n)}” needs a transfer destination.`);
    }
    if (kind === "play_audio" && !cfg.audio_file_id && !cfg.prompt_text?.trim()) errors.push(`Play Audio node “${lbl(n)}” needs an audio file or text prompt.`);
  });
  return errors;
}

function Inner({ initial, onSave, readOnly, saving }) {
  const START_NODE = { id: "start", type: "flowNode", position: { x: 400, y: 0 }, data: { kind: "start", label: "Start", config: {} } };
  const [nodes, setNodes, onNodesChange] = useNodesState(initial?.nodes?.length ? initial.nodes : [START_NODE]);
  const [edges, setEdges, onEdgesChange] = useEdgesState(
    (initial?.edges || []).map((e) => ({ ...e, type: "deletable", data: { ...(e.data || {}), readOnly } }))
  );
  const [selectedId, setSelectedId] = useState(null);
  const [errors, setErrors] = useState([]);
  const nodeTypes = useMemo(() => ({ flowNode: FlowNode }), []);
  const edgeTypes = useMemo(() => ({ deletable: DeletableEdge }), []);

  const onConnect = useCallback((c) => setNodes((nds) => {
    const src = nds.find((n) => n.id === c.source);
    setEdges((eds) => addEdge({ ...c, type: "deletable", data: { readOnly: false }, label: src ? handleLabel(src, c.sourceHandle) : undefined }, eds));
    return nds;
  }), [setEdges, setNodes]);

  const addNode = (kind) => {
    const id = `${kind}_${Date.now().toString(36)}`;
    setNodes((nds) => {
      const maxY = nds.reduce((m, n) => Math.max(m, n.position?.y || 0), 0);
      return nds.concat({
        id, type: "flowNode",
        position: { x: 380, y: maxY + 150 },
        data: { kind, label: KIND[kind].label, config: defaultConfig(kind) },
      });
    });
    setSelectedId(id);
  };

  const selectedNode = nodes.find((n) => n.id === selectedId) || null;

  const save = (keepOpen = false) => {
    const errs = validateFlow(nodes, edges);
    setErrors(errs);
    // "Save flow" finalizes and requires a valid flow. "Save and Continue" saves a
    // work-in-progress draft even when incomplete, so you never lose edits mid-design.
    if (errs.length && !keepOpen) {
      toast.error(`Fix ${errs.length} issue${errs.length === 1 ? "" : "s"} below before saving the flow`);
      return;
    }
    const cleanEdges = edges.map(({ data, ...e }) => {
      const rest = { ...data }; delete rest.readOnly;
      return Object.keys(rest).length ? { ...e, data: rest } : e;
    });
    onSave({ nodes, edges: cleanEdges }, { keepOpen, draft: keepOpen });
  };

  return (
    <div className="flex flex-col h-[70vh]">
      <div className="flex items-center justify-between pb-2">
        <p className="text-xs text-muted-foreground">{nodes.length} nodes · {edges.length} connections</p>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <Button onClick={() => save(true)} disabled={saving} data-testid="save-continue-flow-btn" variant="outline" className="rounded-lg" title="Save your progress (even if incomplete) and keep editing">{saving ? "Saving…" : "Save and Continue"}</Button>
            <Button onClick={() => save(false)} disabled={saving} data-testid="save-flow-btn" className="rounded-lg" title="Validate and save the finished flow, then close">{saving ? "Saving…" : "Save flow"}</Button>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="mb-2 rounded-lg border border-[hsl(var(--warning))]/40 bg-[hsl(var(--warning))]/10 p-3 max-h-40 overflow-y-auto" data-testid="validation-errors">
          <p className="text-sm font-medium text-[hsl(var(--warning))] flex items-center gap-1.5"><WarningCircle className="w-4 h-4" weight="fill" /> {errors.length} item{errors.length === 1 ? "" : "s"} to finish before this flow can go live — <span className="font-normal">“Save and Continue” still saves your draft.</span></p>
          <ul className="mt-1 text-xs text-[hsl(var(--warning))] list-disc pl-5 space-y-0.5">
            {errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      )}

      <div className="flex gap-3 flex-1 min-h-0">
        {!readOnly && (
          <div className="w-40 shrink-0 space-y-1.5 overflow-y-auto pr-1" data-testid="node-palette">
            <p className="text-xs uppercase tracking-wide text-muted-foreground px-1 pb-1">Add node</p>
            {PALETTE.map((k) => (
              <button key={k} onClick={() => addNode(k)} data-testid={`add-node-${k}`}
                className="w-full flex items-center gap-2 rounded-lg border border-border px-2.5 py-2 text-sm hover:bg-muted transition-colors">
                <span className="w-6 h-6 rounded-md grid place-items-center text-white shrink-0" style={{ background: KIND[k].color }}>
                  {(() => { const I = KIND[k].icon; return <I className="w-3.5 h-3.5" weight="bold" />; })()}
                </span>
                {KIND[k].label}
              </button>
            ))}
          </div>
        )}
        <div className="flex-1 rounded-xl border border-border overflow-hidden bg-muted/20 min-w-0" data-testid="flow-canvas">
          <ReactFlow
            nodes={nodes} edges={edges} nodeTypes={nodeTypes} edgeTypes={edgeTypes}
            defaultEdgeOptions={{ type: "deletable", data: { readOnly } }}
            onNodesChange={readOnly ? undefined : onNodesChange}
            onEdgesChange={readOnly ? undefined : onEdgesChange}
            onConnect={readOnly ? undefined : onConnect}
            onNodeClick={(_, n) => setSelectedId(n.id)}
            onPaneClick={() => setSelectedId(null)}
            nodesDraggable={!readOnly} nodesConnectable={!readOnly} elementsSelectable={!readOnly}
            edgesFocusable={!readOnly}
            fitView proOptions={{ hideAttribution: true }}
          >
            <Background gap={16} />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable />
          </ReactFlow>
        </div>
        {!readOnly && selectedNode && (
          <ConfigPanel node={selectedNode} setNodes={setNodes} setEdges={setEdges} onClose={() => setSelectedId(null)} />
        )}
      </div>
    </div>
  );
}

export default function FlowDesigner(props) {
  return (
    <ReactFlowProvider>
      <Inner {...props} />
    </ReactFlowProvider>
  );
}

// Phrases editor for Customer Response branches. Keeps a LOCAL text buffer so
// typing a space between words is never swallowed (the old version trimmed on
// every keystroke, turning "yes i am interested" into "yesiaminterested").
// The comma-separated list is parsed into the phrases array only on blur.
function PhrasesInput({ value, onCommit, testid }) {
  const [text, setText] = useState((value || []).join(", "));
  useEffect(() => {
    const parsed = text.split(",").map((s) => s.trim()).filter(Boolean);
    if ((value || []).join("\u0000") !== parsed.join("\u0000")) {
      setText((value || []).join(", "));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);
  const commit = () => onCommit(text.split(",").map((s) => s.trim()).filter(Boolean));
  return (
    <Input
      className="h-8 text-xs"
      data-testid={testid}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      placeholder="phrases: yes, i'm interested, sure"
    />
  );
}
